// My work: the Studio's desk. The running timer is the hero and glows while it runs;
// everything under it is quiet and ordered by what needs a person next. Behaviour lives in
// web/docs/features/spark.md.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import { useToast } from '../components/Toast/ToastContext';
import { Alert } from '../components/ui/Alert';
import { Button, buttonClass } from '../components/ui/Button';
import { EmptyMark, EmptyState } from '../components/ui/EmptyState';
import { Field } from '../components/ui/Field';
import { IconChevron, IconClock, IconPlay, IconSpark, IconStop } from '../components/ui/icons';
import { MS, useExiting, useTicker } from '../components/ui/motion';
import type { Exiting } from '../components/ui/motion';
import { PageLayout } from '../components/ui/PageLayout';
import { Panel } from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import { Skeleton } from '../components/ui/Skeleton';
import { Tag } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import { Tooltip } from '../components/ui/Tooltip';
import { businessToday, formatEventDate, formatTimestamp, parseDate } from '../utils/date-utils';
import { liveScope, useLiveScopes } from '../live/useLiveScopes';
import { confirmTime, editTime, getMyWork, startTimer, stopTimer } from './sparkApi';
import type { MyWork, TimeEntry } from './sparkApi';
import { ReviewRequestCard, TaskStatusTag } from './SparkBits';

type Task = MyWork['tasks'][number];

const errorText = (err: unknown, fallback: string) => {
  const e = err as { data?: { error?: { message?: string } | string }; message?: string } | null;
  const d = e?.data?.error;
  if (typeof d === 'string') return d;
  return d?.message || fallback;
};

/** A ticking "now", only while something needs it. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);
  return now;
}

const pad = (n: number) => String(n).padStart(2, '0');

function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

function span(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return '';
  const mins = Math.max(0, Math.round(dayjs(endedAt).diff(dayjs(startedAt), 'minute', true)));
  const h = Math.floor(mins / 60);
  return h ? `${h}h ${pad(mins % 60)}m` : `${mins}m`;
}

const STAMP = 'MMM D, h:mm A';
const LOCAL_INPUT = 'YYYY-MM-DDTHH:mm';
const GRID = 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,24rem),1fr))] items-start gap-x-4';
const QUIET_LINK = 'min-w-0 truncate rounded-inner text-xs text-on-surface-variant transition-colors duration-quick ease-emphasized hover:text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

// ── Arriving and leaving ─────────────────────────────────────────────────────

/** Grows in when it arrived after load, dissolves when `show` ends. */
const Presence: React.FC<{ show: boolean; fresh: boolean; children: React.ReactNode }> = ({ show, fresh, children }) => {
  const [on, setOn] = useState(!fresh);
  useEffect(() => {
    if (!fresh) return undefined;
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, [fresh]);
  return <Reveal show={show && on}>{children}</Reveal>;
};

const Count: React.FC<{ n: number; label: string }> = ({ n, label }) => {
  const shown = useTicker(n);
  return <span aria-label={label} className="text-xs font-medium tabular-nums text-on-surface-variant">{shown}</span>;
};

// ── The running timer ────────────────────────────────────────────────────────

function TimerHero({ running, busy, onStop }: { running: TimeEntry | null; busy: boolean; onStop: () => void }) {
  const now = useNow(!!running);
  if (!running) return null;
  const elapsed = clock(now - dayjs(running.started_at).valueOf());
  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-panel bg-surface-container-high p-4 shadow-glow">
        <div className="min-w-0 flex-1 basis-56">
          <div className="flex items-center gap-2 text-xs text-primary">
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-primary shadow-glow motion-safe:animate-pulse" />
            Timer running
          </div>
          <div className="mt-1 truncate text-base font-semibold text-on-surface">{running.task_name}</div>
          <Link to={`/spark/projects/${running.production_id}`} className={QUIET_LINK}>
            {running.production_name}
          </Link>
        </div>
        <span
          className="font-mono text-4xl font-semibold leading-none tabular-nums text-on-surface drop-shadow-glow"
          role="timer"
          aria-label={`Elapsed ${elapsed}`}
        >
          {elapsed}
        </span>
        <Tooltip label="Stop the timer and save this time">
          <Button variant="filled" leading={<IconStop className="h-3 w-3" />} onClick={onStop} disabled={busy}>
            Stop and save
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Times to confirm ─────────────────────────────────────────────────────────

function ConfirmRow({ entry, onChanged }: { entry: TimeEntry; onChanged: () => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(dayjs(entry.started_at).format(LOCAL_INPUT));
  const [end, setEnd] = useState(entry.ended_at ? dayjs(entry.ended_at).format(LOCAL_INPUT) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    try {
      await confirmTime(entry.id);
      toast.success('Time confirmed.');
      onChanged();
    } catch (err) {
      toast.error(errorText(err, 'That time did not confirm. Try again.'));
      setBusy(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!start || !end) {
      setError('Fill in both times.');
      return;
    }
    if (!dayjs(end).isAfter(dayjs(start))) {
      setError('The end has to come after the start.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await editTime(entry.id, dayjs(start).toISOString(), dayjs(end).toISOString());
      toast.success('Time updated and confirmed.');
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(errorText(err, 'That change did not save. Try again.'));
      setBusy(false);
    }
  };

  return (
    <div className="rounded-panel bg-surface-container p-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-56">
          <div className="truncate text-sm font-medium text-on-surface">{entry.task_name}</div>
          <Link to={`/spark/projects/${entry.production_id}`} className={`${QUIET_LINK} block`}>
            {entry.production_name}
          </Link>
          <div className="mt-1 text-xs tabular-nums text-on-surface-variant">
            {formatTimestamp(entry.started_at, STAMP)} to {entry.ended_at ? formatTimestamp(entry.ended_at, STAMP) : 'still open'}
            <span className="ml-2 font-semibold text-on-surface">{span(entry.started_at, entry.ended_at)}</span>
          </div>
          {entry.previous && (
            <div className="mt-0.5 text-xs text-on-surface-variant">
              Was {formatTimestamp(entry.previous.started_at, STAMP)} to {formatTimestamp(entry.previous.ended_at, STAMP)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Tooltip label="Keep these times as they are">
            <Button variant="tonal" size="sm" onClick={confirm} disabled={busy}>Confirm</Button>
          </Tooltip>
          <Tooltip label={editing ? 'Close the time editor' : 'Change the start or end before confirming'}>
            <Button variant="quiet" size="sm" onClick={() => { setEditing((v) => !v); setError(null); }} disabled={busy} aria-expanded={editing}>
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          </Tooltip>
        </div>
      </div>
      <Reveal show={editing}>
        <form onSubmit={save} className="flex flex-wrap items-end gap-3 pt-3">
          <label className="text-xs text-on-surface-variant">
            Start
            <Field type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 !w-auto" />
          </label>
          <label className="text-xs text-on-surface-variant">
            End
            <Field type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 !w-auto" />
          </label>
          <Button type="submit" variant="filled" disabled={busy}>Save times</Button>
          <div className="-mt-3 basis-full">
            <Reveal show={!!error}>
              <Alert tone="error" variant="text" className="pt-3">{error}</Alert>
            </Reveal>
          </div>
        </form>
      </Reveal>
    </div>
  );
}

// ── Deliverables ─────────────────────────────────────────────────────────────

type GroupKey = 'overdue' | 'week' | 'later' | 'none' | 'ready';
const GROUPS: { key: GroupKey; label: string }[] = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'week', label: 'Due this week' },
  { key: 'later', label: 'Later' },
  { key: 'none', label: 'No date' },
  { key: 'ready', label: 'Ready to Fly' },
];

function groupOf(t: Task): GroupKey {
  if (t.status === 'ready' || t.status === 'closed') return 'ready';
  if (!t.due_date) return 'none';
  const days = parseDate(t.due_date).diff(businessToday(), 'day');
  return days < 0 ? 'overdue' : days <= 6 ? 'week' : 'later';
}

/** The due date in words, and whether it is overdue. */
function dueIn(t: Task): { text: string; overdue: boolean } {
  if (!t.due_date) return { text: 'No due date', overdue: false };
  if (groupOf(t) === 'ready') return { text: `Due ${formatEventDate(t.due_date)}`, overdue: false };
  const days = parseDate(t.due_date).diff(businessToday(), 'day');
  if (days < 0) return { text: `${-days} ${days === -1 ? 'day' : 'days'} overdue`, overdue: true };
  if (days === 0) return { text: 'Due today', overdue: false };
  if (days === 1) return { text: 'Due tomorrow', overdue: false };
  if (days <= 6) return { text: `Due in ${days} days`, overdue: false };
  return { text: `Due ${formatEventDate(t.due_date)}`, overdue: false };
}

function DeliverableRow({ task, runningId, busy, onStart }: { task: Task; runningId: string | null; busy: boolean; onStart: (id: string) => void }) {
  const done = task.status === 'ready' || task.status === 'closed';
  const due = dueIn(task);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-panel bg-surface-container px-3 py-2.5">
      <div className="min-w-0 flex-1 basis-40">
        <div className="truncate text-sm font-medium text-on-surface">{task.name}</div>
        <Link to={`/spark/projects/${task.production_id}`} className={`${QUIET_LINK} block`}>
          {task.production_name}
        </Link>
      </div>
      <div className="flex flex-none items-center gap-2">
        <span className="whitespace-nowrap text-xs tabular-nums text-on-surface-variant">
          {due.overdue ? <Tag hex={CATEGORY.red.ink.hex}>{due.text}</Tag> : due.text}
        </span>
        <TaskStatusTag status={task.status} />
      </div>
      <div className="flex w-[5.5rem] flex-none justify-end">
        {done ? null : runningId === task.id ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary drop-shadow-glow">
            <IconClock className="h-3.5 w-3.5" />
            Running
          </span>
        ) : (
          <Tooltip label={`Start the timer on ${task.name}`}>
            <Button variant="tonal" size="sm" onClick={() => onStart(task.id)} disabled={busy} aria-label={`Start timer on ${task.name}`} leading={<IconPlay className="h-2.5 w-2.5" />}>
              Start
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function DeliverableGroup({
  groupKey, label, entries, open, onToggle, runningId, busy, onStart,
}: {
  groupKey: GroupKey; label: string; entries: Exiting<Task>[]; open: boolean; onToggle: () => void;
  runningId: string | null; busy: boolean; onStart: (id: string) => void;
}) {
  const live = entries.filter((e) => !e.leaving).length;
  const overdue = groupKey === 'overdue';
  const bodyId = `mw-body-${groupKey}`;
  return (
    <section aria-labelledby={`mw-${groupKey}`} className="min-w-0 pb-4">
      <h3 id={`mw-${groupKey}`} className="m-0 mb-2 text-sm font-semibold">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
          className={`np-state -ml-1.5 inline-flex items-center gap-1.5 rounded-control px-1.5 py-1 transition-[background-color,color,--np-layer] duration-quick ease-emphasized focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${overdue ? 'text-red-300' : 'text-on-surface'}`}
        >
          <IconChevron open={open} className="h-3.5 w-3.5 transition-transform duration-standard ease-emphasized motion-reduce:transition-none" />
          {label}
          <Count n={live} label={`${live} deliverable${live === 1 ? '' : 's'}`} />
        </button>
      </h3>
      <div id={bodyId}>
        <Reveal show={open}>
          <div>
            {entries.map((e) => (
              <Presence key={e.key} show={!e.leaving} fresh={e.fresh}>
                <div className="pb-2">
                  <DeliverableRow task={e.item} runningId={runningId} busy={busy} onStart={onStart} />
                </div>
              </Presence>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const SparkMyWork: React.FC = () => {
  const toast = useToast();
  const [data, setData] = useState<MyWork | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shut, setShut] = useState<Record<string, boolean>>({ ready: true });

  // Only the newest getMyWork answer lands: a live refetch sent before a timer's reload can arrive after it.
  const latest = useRef(0);
  const fetchWork = useCallback(async () => {
    const n = ++latest.current;
    const work = await getMyWork();
    if (latest.current === n) { setData(work); setError(null); }
  }, []);

  const load = useCallback(async () => {
    try {
      await fetchWork();
    } catch (err) {
      setError(errorText(err, 'Your work did not load. Check your connection and try again.'));
    }
  }, [fetchWork]);

  useEffect(() => { load(); }, [load]);
  // Anything new in Spark (a review sent to me, a deliverable assigned) lands in place; rows enter and leave through useExiting.
  // A failure leaves the page as it is; the rejection tells the poller to try again.
  useLiveScopes([liveScope.spark], () => fetchWork());

  const taskItems = data ? data.tasks : null;
  // A Reveal collapse starts a commit after the row turns leaving, so the row outlives MS.exit a little.
  const taskEntries = useExiting(taskItems, (t) => `${groupOf(t)}:${t.id}`, MS.standard);
  const confirmEntries = useExiting(data ? data.to_confirm : null, (e) => String(e.id), MS.standard);
  const reviewEntries = useExiting(data ? data.reviews : null, (r) => r.id, MS.standard);

  const groups = useMemo(() => (
    GROUPS.map((g) => ({
      ...g,
      entries: taskEntries
        .filter((e) => e.key.startsWith(`${g.key}:`))
        .sort((a, b) => (a.item.due_date ?? '9999').localeCompare(b.item.due_date ?? '9999')),
    })).filter((g) => g.entries.length > 0)
  ), [taskEntries]);

  const previewsDue = useMemo(() => {
    const today = businessToday();
    const ids = new Set<string>();
    (data?.tasks ?? []).forEach((t) => {
      if (!t.preview_due) return;
      const days = parseDate(t.preview_due).diff(today, 'day');
      if (days >= 0 && days <= 7) ids.add(t.production_id);
    });
    return ids.size;
  }, [data]);

  const onStart = async (taskId: string) => {
    setBusy(true);
    try {
      await startTimer(taskId);
      await load();
    } catch (err) {
      toast.error(errorText(err, 'The timer did not start. Try again.'));
    }
    setBusy(false);
  };

  const onStop = async () => {
    setBusy(true);
    try {
      const { entry } = await stopTimer();
      if (entry.needs_confirm) toast.info('That timer crossed midnight. Check the times below, then confirm.');
      else toast.success('Time saved.');
      await load();
    } catch (err) {
      toast.error(errorText(err, 'The timer did not stop. Try again.'));
    }
    setBusy(false);
  };

  const header = (
    <PageHeader
      title="My work"
      icon={<IconSpark />}
      lede="What is waiting on you and what is on your plate, soonest first."
      back={{ to: '/spark', label: 'Spark' }}
    />
  );

  if (error && !data) {
    return (
      <PageLayout width="full" kind="workspace" header={header}>
        <Alert
          tone="error"
          className="mt-4"
          onRetry={load}
          action={<Link to="/spark" className={buttonClass('quiet')}>Back to Spark</Link>}
        >
          {error}
        </Alert>
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout width="full" kind="workspace" header={header}>
        <div className="mt-4 space-y-4" role="status" aria-label="Loading your work">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
          <Skeleton className="h-52" />
        </div>
      </PageLayout>
    );
  }

  const taskCount = data.tasks.length;
  const nothing = !data.running && data.to_confirm.length === 0 && data.reviews.length === 0 && taskCount === 0;
  const runningId = data.running?.task_id ?? null;

  return (
    <PageLayout width="full" kind="workspace" header={header}>
      <div className="mt-4">
        <Reveal show={nothing}>
          <div className="pb-4">
            <EmptyState
              mark={<EmptyMark />}
              title="Nothing is waiting on you"
              action={<Link to="/spark" className={buttonClass('filled')}>Open Spark</Link>}
            >
              Deliverables assigned to you, reviews that reach you and timers that need a check all land here. Open a project in Spark and take a deliverable to get started.
            </EmptyState>
          </div>
        </Reveal>

        <Reveal show={!!data.running}>
          <TimerHero running={data.running} busy={busy} onStop={onStop} />
        </Reveal>

        <Reveal show={confirmEntries.some((e) => !e.leaving)}>
          <div className="pb-4">
            <Panel
              title={<span className="inline-flex items-baseline gap-2">Confirm these times <Count n={confirmEntries.filter((e) => !e.leaving).length} label="times to confirm" /></span>}
              actions={<span className="text-xs text-on-surface-variant">A timer ran past midnight, so Spark is asking</span>}
            >
              <div className={GRID}>
                {confirmEntries.map((e) => (
                  <Presence key={e.key} show={!e.leaving} fresh={e.fresh}>
                    <div className="pb-2"><ConfirmRow entry={e.item} onChanged={load} /></div>
                  </Presence>
                ))}
              </div>
            </Panel>
          </div>
        </Reveal>

        <Reveal show={reviewEntries.some((e) => !e.leaving)}>
          <div className="pb-4">
            <Panel
              title={<span className="inline-flex items-baseline gap-2">Reviews waiting on you <Count n={reviewEntries.filter((e) => !e.leaving).length} label="reviews waiting" /></span>}
              actions={<span className="text-xs text-on-surface-variant">Spark also sent you a Slack message for each</span>}
            >
              <div className={GRID}>
                {reviewEntries.map((e) => (
                  <Presence key={e.key} show={!e.leaving} fresh={e.fresh}>
                    <div className="pb-3"><ReviewRequestCard review={e.item} onDone={load} /></div>
                  </Presence>
                ))}
              </div>
            </Panel>
          </div>
        </Reveal>

        <Reveal show={taskCount > 0}>
          <Panel
            title={<span className="inline-flex items-baseline gap-2">Your deliverables <Count n={taskCount} label={`${taskCount} in total`} /></span>}
            actions={previewsDue > 0 ? (
              <span className="text-xs tabular-nums text-on-surface-variant">
                {previewsDue} preview{previewsDue === 1 ? '' : 's'} due in the next 7 days
              </span>
            ) : undefined}
          >
            <div className={GRID}>
              {groups.map((g) => (
                <Presence key={g.key} show={g.entries.some((e) => !e.leaving)} fresh={g.entries.every((e) => e.fresh)}>
                  <DeliverableGroup
                    groupKey={g.key}
                    label={g.label}
                    entries={g.entries}
                    open={!shut[g.key]}
                    onToggle={() => setShut((s) => ({ ...s, [g.key]: !s[g.key] }))}
                    runningId={runningId}
                    busy={busy}
                    onStart={onStart}
                  />
                </Presence>
              ))}
            </div>
          </Panel>
        </Reveal>
      </div>
    </PageLayout>
  );
};

export default SparkMyWork;
